import React, { useEffect, useState } from "react";
import { PoundSterling, Wallet, TrendingUp, Truck, Sparkles, Package } from "lucide-react";
import { api } from "../../lib/api";
import { cedis } from "./ui";

function Ledger({ icon: Icon, title, subtitle, rows, footer, tone }) {
  return (
    <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid={`ledger-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1"><Icon size={18} className="text-[#306D29]" /><h3 className="font-display text-xl text-[#0D530E]">{title}</h3></div>
      <p className="text-xs text-[#5C5C5C] mb-4">{subtitle}</p>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between text-sm">
            <span className="text-[#5C5C5C]">{r.label}</span>
            <span className="font-medium">{r.value}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-black/10 mt-4 pt-3 flex justify-between items-baseline">
        <span className="font-semibold text-[#0D530E]">{footer.label}</span>
        <span className={`font-display text-2xl ${tone || "text-[#306D29]"}`} data-testid={footer.testid}>{footer.value}</span>
      </div>
    </div>
  );
}

export default function Finance() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/finance").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-[#5C5C5C]">Loading…</p>;
  const fx = d.fx_gbp_ghs;
  const g = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pl = d.product_ledger, ll = d.logistics_ledger, sl = d.service_ledger;

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Ledger icon={Wallet} title="Product Ledger" subtitle="Money that passes through to UK stores — not your income."
          rows={[
            { label: "Charged to customers", value: cedis(pl.charged, fx) },
            { label: "Paid to stores (actual)", value: cedis(pl.paid_to_stores, fx) },
            { label: "Items purchased", value: pl.items_purchased },
          ]}
          footer={{ label: "Variance", value: cedis(pl.variance, fx), testid: "ledger-product-variance" }}
          tone={pl.variance >= 0 ? "text-[#2D7A5D]" : "text-[#B23B3B]"} />

        <Ledger icon={Truck} title="Logistics Ledger" subtitle="Shipping & customs — charged vs your real carrier spend."
          rows={[
            { label: "Shipping charged", value: cedis(ll.charged, fx) },
            { label: "Actual logistics cost", value: cedis(ll.actual_cost, fx) },
            { label: "Packages", value: ll.packages },
          ]}
          footer={{ label: "Logistics margin", value: cedis(ll.margin, fx), testid: "ledger-logistics-margin" }}
          tone={ll.margin >= 0 ? "text-[#2D7A5D]" : "text-[#B23B3B]"} />

        <Ledger icon={Sparkles} title="Service Revenue" subtitle="Your true income — handling & service-level fees."
          rows={[
            { label: "Handling fees", value: cedis(sl.handling, fx) },
            { label: "Service level fees", value: cedis(sl.service_levels, fx) },
            { label: "Paid orders", value: d.orders_paid },
          ]}
          footer={{ label: "Service revenue", value: cedis(sl.total, fx), testid: "ledger-service-total" }} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-[#0D530E] text-white rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2 text-white/60"><TrendingUp size={16} /><span className="text-xs uppercase tracking-wider">Net profit</span></div>
          <p className="font-display text-3xl" data-testid="finance-net-profit">{cedis(d.net_profit_gbp, fx)}</p>
          <p className="text-xs text-white/50 mt-1">£{d.net_profit_gbp.toLocaleString()} · service revenue + logistics margin + product variance</p>
        </div>
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2 text-[#5C5C5C]"><PoundSterling size={16} /><span className="text-xs uppercase tracking-wider">Gross revenue</span></div>
          <p className="font-display text-3xl text-[#0D530E]">{cedis(d.revenue_gbp, fx)}</p>
          <p className="text-xs text-[#5C5C5C] mt-1">{g(d.revenue_gbp)} · all money collected</p>
        </div>
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2 text-[#5C5C5C]"><Package size={16} /><span className="text-xs uppercase tracking-wider">Orders paid</span></div>
          <p className="font-display text-3xl text-[#0D530E]">{d.orders_paid}</p>
          <p className="text-xs text-[#5C5C5C] mt-1">{pl.items_purchased} items sourced</p>
        </div>
      </div>
      <p className="text-sm text-[#5C5C5C]">Record real costs in Purchasing (product) and on each Shipping package (logistics cost £) to keep these ledgers accurate.</p>
    </div>
  );
}
